import { NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { NavigationHelper } from 'app/core/services/navigation-helper';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { findData } from 'app/core/utils/route-helper';
import { AddMenuModule } from 'app/layout/common/add-menu/add-menu.module';
import { HelpModule } from 'app/layout/common/help/help.module';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { QuickChatComponent } from 'app/layout/common/quick-chat/quick-chat.component';
import { SearchBarModule } from 'app/layout/common/search-bar/search-bar.module';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { SettingsModule } from 'app/layout/common/settings/settings.module';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { UserMenuModule } from 'app/layout/common/user-menu/user-menu.module';
import { UserComponent } from 'app/layout/common/user/user.component';
import { Admin } from 'app/model';
import { SharedModule } from 'app/shared/shared.module';
import { environment } from 'environments/environment';
import _ from 'lodash-es';
import { Subject, filter, map, startWith, takeUntil } from 'rxjs';

@Component({
    selector     : 'classy-layout',
    templateUrl  : './classy.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone   : true,
    imports      : [
      FuseLoadingBarComponent, FuseVerticalNavigationComponent, NotificationsComponent, UserComponent,
      NgIf, MatIconModule, MatButtonModule, LanguagesComponent, FuseFullscreenComponent, SearchComponent,
      ShortcutsComponent, MessagesComponent, RouterOutlet, QuickChatComponent, AddMenuModule, UserMenuModule,
      SettingsModule, SearchBarModule, HelpModule, SharedModule
    ],
})
export class ClassyLayoutComponent implements OnInit, OnDestroy
{
    initialNavJson: string;
    job: Admin;
    isScreenSmall: boolean;
    navigation: Navigation;
    user: User;
    hideNavigation: boolean = false;
    pageTitle: string;
    practiceName: string;
    /** Sidebar profile avatar is replaced with the Insolv logo for this theme. */
    isInsolvTheme: boolean = environment.theme === 'theme-aryza-insolv2-uk-theme';
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _userService: UserService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _navigationHelper: NavigationHelper,
        private titleService: Title
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number
    {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // subscribe to router events and hide navigation for Insol pages
        this._router.events
          .pipe(
            takeUntil(this._unsubscribeAll),
            filter((evt) => evt instanceof NavigationEnd),
            map((evt: NavigationEnd) => evt.url),
            startWith(this._router.url)
          )
          .subscribe((url) => {
            //this.hideNavigation = url.startsWith('/insol');

            // Get title from data, for display in header
            let route: ActivatedRoute = this._router.routerState.root;
            while (route.firstChild) {
              route = route.firstChild;
            }
            if (route.snapshot.data['title']) {
              // put { data: { title: 'my title' }} on routes for custom title
              this.pageTitle = route.snapshot.data['title'];
            } else {
              // default is route path as title
              this.pageTitle = _.startCase(route.snapshot.routeConfig.path);
            }

          });

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) =>
            {
                this.navigation = navigation;
                if (!this.initialNavJson) {
                  this.initialNavJson = JSON.stringify(navigation.default);
                }
            });

        // Subscribe to the user service
        this._userService.user$
            .pipe((takeUntil(this._unsubscribeAll)))
            .subscribe((user: User) =>
            {
                this.user = user;
                this.practiceName = user.practiceName;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) =>
            {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void
    {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if ( navigation )
        {
            // Toggle the opened status
            navigation.toggle();
        }
    }


  /** get the job from data on the route, and setup the navigation */
  private setJob(snapshot: ActivatedRouteSnapshot): void {
    const job = findData<Admin>(snapshot, 'job');
    if (job) {
      this.job = job;
      this._navigationHelper.addJobNavitem(job, this.navigation.default);

      this.titleService.setTitle(`${environment.appName} | ${job.shortName}`);
    } else {
      this.titleService.setTitle(environment.appName);
    }
  }
}
